/**
 * Payroll module constants and helper functions.
 * Shared across all payroll components to maintain single source of truth.
 */

export const DEFAULT_META = {
  money: {
    currency: "INR",
    storageUnit: "paise",
    displayUnit: "rupees",
    scale: 100,
  },
};

export const DEFAULT_COMPONENTS = [
  {
    key: "BASIC",
    type: "EARNING",
    calculationType: "FIXED",
    value: 25000,
    formula: "",
    isTaxable: true,
    isPFApplicable: true,
    isESIApplicable: true,
  },
  {
    key: "HRA",
    type: "EARNING",
    calculationType: "PERCENT_OF_BASIC",
    value: 40,
    formula: "",
    isTaxable: true,
    isPFApplicable: false,
    isESIApplicable: true,
  },
];

export const BLANK_COMPONENT = {
  key: "",
  type: "EARNING",
  calculationType: "FIXED",
  value: 0,
  formula: "",
  isTaxable: true,
  isPFApplicable: false,
  isESIApplicable: false,
};

export const DEFAULT_PT_SLABS_JSON = JSON.stringify(
  [
    { min: 0, max: 14999, tax: 0 },
    { min: 15000, max: null, tax: 200 },
  ],
  null,
  2,
);

export const SECTION_CONFIG = {
  runs: {
    title: "Payroll Run",
    description:
      "Create payroll runs, process deterministic snapshots, and finalize immutable payroll artifacts.",
  },
  ctc: {
    title: "Salary Structures",
    description:
      "Manage salary structures and employee compensation mappings for tenant-scoped payroll.",
  },
  compliance: {
    title: "Statutory & Compliance",
    description:
      "Configure PF, ESI, PT, and track filing and payment workflow for statutory records.",
  },
  benefits: {
    title: "Benefits & Adjustments",
    description:
      "Capture reimbursements, bonus, gratuity, and arrears before payroll is processed.",
  },
  fnf: {
    title: "Full & Final Settlement",
    description:
      "Prepare and settle employee exit payroll with auditable earnings and deductions breakups.",
  },
  form16: {
    title: "Form 16",
    description:
      "Generate financial-year tax metadata and issue Form 16 records by employee.",
  },
  bank: {
    title: "Bank Transfers",
    description:
      "Review payout totals, track transfer status, and preview employee bank disbursement rows.",
  },
  payslips: {
    title: "Payslips",
    description:
      "Review finalized payslips. Amounts are stored in paise and displayed here in rupees.",
  },
};

/**
 * Retrieve user from localStorage with error handling.
 */
export const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

/**
 * Determine active section key from current route pathname.
 */
export const getSectionKey = (pathname) => {
  if (pathname.includes("/payroll/ctc")) return "ctc";
  if (pathname.includes("/payroll/statutory")) return "compliance";
  if (
    pathname.includes("/payroll/benefits") ||
    pathname.endsWith("/benefits")
  ) {
    return "benefits";
  }
  if (
    pathname.includes("/payroll/fnf") ||
    pathname.includes("/exit/fnf-payroll")
  ) {
    return "fnf";
  }
  if (pathname.includes("/payroll/form16")) return "form16";
  if (pathname.includes("/payroll/bank")) return "bank";
  if (pathname.includes("/payroll/reports")) return "compliance";
  if (pathname.includes("/ess/payslips") || pathname.endsWith("/pay"))
    return "payslips";
  return "runs";
};

/**
 * Form resetters: return initial blank form state for each section.
 */

export const resetStructureForm = () => ({
  name: "",
  code: "",
  ctc: "",
  effectiveFrom: "",
  components: DEFAULT_COMPONENTS.map((c) => ({ ...c })),
});

export const resetCompensationForm = () => ({
  userId: "",
  salaryStructureId: "",
  baseSalary: "",
  effectiveFrom: "",
  variablesJson: "{}",
});

export const resetRunForm = () => ({
  month: String(new Date().getMonth() + 1),
  year: String(new Date().getFullYear()),
});

export const resetBenefitForm = () => ({
  userId: "",
  type: "Internet",
  amount: "",
  month: String(new Date().getMonth() + 1),
  year: String(new Date().getFullYear()),
  status: "APPROVED",
});

export const resetAdjustmentForm = () => ({
  userId: "",
  type: "BONUS",
  amount: "",
  effectiveMonth: String(new Date().getMonth() + 1),
  effectiveYear: String(new Date().getFullYear()),
  note: "",
});

export const resetFnfForm = () => ({
  userId: "",
  relievingDate: "",
  unpaidSalary: "",
  leaveEncashment: "",
  gratuity: "",
  deductions: "",
  note: "",
  status: "DRAFT",
});

export const resetForm16Form = () => ({
  userId: "",
  financialYear: `${new Date().getFullYear() - 1}-${new Date().getFullYear()}`,
  fileUrl: "",
  fileName: "",
  notes: "",
  status: "GENERATED",
});
